import SvgAsset from '../SvgAsset';
import { ErrorMessage } from './FeedbackMessage';
import LabelWithTooltip from './LabelWithTooltip';
import type { AuthTemplateProps, EvaluatedPasswordRequirement } from './types';
import {
    type FormFieldErrorVariant,
    formFieldErrorVariantClasses,
    resolveFormFieldErrorVariant,
} from './formFieldErrorVariants';

const checkThickSolidSrc = '/auth/assets/svgs/icons/check-thick-solid.svg';

type FieldProps = Pick<
    AuthTemplateProps,
    | 'form'
    | 'update'
    | 'markTouched'
    | 'getFieldError'
    | 'getFieldValidationState'
    | 'fieldErrorVariant'
>;

export function FieldError({ id, message }: { id: string; message?: string }) {
    return <ErrorMessage id={id} message={message} />;
}

function joinClasses(...classes: Array<string | false | null | undefined>) {
    return classes.filter(Boolean).join(' ');
}

function getErrorVariantClasses(error?: string, variant?: FormFieldErrorVariant) {
    if (!error) return {};
    return formFieldErrorVariantClasses[resolveFormFieldErrorVariant(variant)];
}

function fieldClass(error?: string, variant?: FormFieldErrorVariant) {
    const errorVariantClasses = getErrorVariantClasses(error, variant);

    return joinClasses(
        'form-field',
        error && 'form-field--error',
        error && errorVariantClasses.container,
    );
}

function inputClass(error?: string, variant?: FormFieldErrorVariant) {
    const errorVariantClasses = getErrorVariantClasses(error, variant);

    return joinClasses('input light', error && errorVariantClasses.control);
}

function passwordFieldClass(error?: string, variant?: FormFieldErrorVariant) {
    return joinClasses('mt-3 p-5 bg-marshmallow rounded-xl', fieldClass(error, variant));
}

export function NameFields({
    form,
    update,
    markTouched,
    getFieldError,
    getFieldValidationState,
    fieldErrorVariant,
}: FieldProps) {
    const firstNameError = getFieldError('firstName');
    const lastNameError = getFieldError('lastName');

    return (
        <div className="grid gap-3 sm:grid-cols-2">
            <div className={fieldClass(firstNameError, fieldErrorVariant)}>
                <LabelWithTooltip
                    label="First name"
                    htmlFor="firstName"
                    validationState={getFieldValidationState('firstName')}
                />
                <input
                    id="firstName"
                    className={inputClass(firstNameError, fieldErrorVariant)}
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={update('firstName')}
                    onBlur={markTouched('firstName')}
                    aria-invalid={firstNameError ? 'true' : undefined}
                    aria-describedby={firstNameError ? 'firstName-error' : undefined}
                />
                <FieldError id="firstName-error" message={firstNameError} />
            </div>
            <div className={fieldClass(lastNameError, fieldErrorVariant)}>
                <LabelWithTooltip
                    label="Last name"
                    htmlFor="lastName"
                    validationState={getFieldValidationState('lastName')}
                />
                <input
                    id="lastName"
                    className={inputClass(lastNameError, fieldErrorVariant)}
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={update('lastName')}
                    onBlur={markTouched('lastName')}
                    aria-invalid={lastNameError ? 'true' : undefined}
                    aria-describedby={lastNameError ? 'lastName-error' : undefined}
                />
                <FieldError id="lastName-error" message={lastNameError} />
            </div>
        </div>
    );
}

export function EmailField({
    form,
    update,
    markTouched,
    getFieldError,
    getFieldValidationState,
    fieldErrorVariant,
}: FieldProps) {
    const emailError = getFieldError('email');

    return (
        <div className={fieldClass(emailError, fieldErrorVariant)}>
            <LabelWithTooltip
                label="Email"
                htmlFor="email"
                validationState={getFieldValidationState('email')}
            />
            <input
                id="email"
                className={inputClass(emailError, fieldErrorVariant)}
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={update('email')}
                onBlur={markTouched('email')}
                aria-invalid={emailError ? 'true' : undefined}
                aria-describedby={emailError ? 'email-error' : undefined}
            />
            <FieldError id="email-error" message={emailError} />
        </div>
    );
}

export function PasswordRequirementsField({
    form,
    update,
    markTouched,
    getFieldError,
    getFieldValidationState,
    fieldErrorVariant,
    requirements,
    label,
}: FieldProps & { requirements: EvaluatedPasswordRequirement[]; label: string }) {
    const passwordError = getFieldError('password');

    return (
        <div className={passwordFieldClass(passwordError, fieldErrorVariant)}>
            <LabelWithTooltip
                label={label}
                htmlFor="password"
                validationState={getFieldValidationState('password')}
            />
            <input
                id="password"
                className={inputClass(passwordError, fieldErrorVariant)}
                type="password"
                autoComplete="new-password"
                required
                value={form.password}
                onChange={update('password')}
                onBlur={markTouched('password')}
                aria-invalid={passwordError ? 'true' : undefined}
                aria-describedby={
                    passwordError ? 'password-error password-requirements' : 'password-requirements'
                }
            />
            <FieldError id="password-error" message={passwordError} />
            <ul id="password-requirements" className="mt-5 space-y-2 text-sm" aria-live="polite">
                {requirements.map((rule) => (
                    <li key={rule.id} className="flex items-center gap-2">
                        <span
                            aria-hidden="true"
                            className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                                rule.met
                                    ? 'border-licorice text-licorice'
                                    : 'border-licorice/50 text-licorice/50'
                            }`}
                        >
                            {rule.met ? (
                                <SvgAsset src={checkThickSolidSrc} className="w-2 h-2" />
                            ) : null}
                        </span>
                        <span className={rule.met ? 'text-licorice' : 'text-licorice/30'}>
                            {rule.label}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function PasswordField({
    form,
    update,
    markTouched,
    getFieldError,
    getFieldValidationState,
    fieldErrorVariant,
    label,
    autoComplete,
}: FieldProps & { label: string; autoComplete: 'current-password' | 'new-password' }) {
    const passwordError = getFieldError('password');

    return (
        <div className={fieldClass(passwordError, fieldErrorVariant)}>
            <LabelWithTooltip
                label={label}
                htmlFor="password"
                validationState={getFieldValidationState('password')}
            />
            <input
                id="password"
                className={inputClass(passwordError, fieldErrorVariant)}
                type="password"
                autoComplete={autoComplete}
                required
                value={form.password}
                onChange={update('password')}
                onBlur={markTouched('password')}
                aria-invalid={passwordError ? 'true' : undefined}
                aria-describedby={passwordError ? 'password-error' : undefined}
            />
            <FieldError id="password-error" message={passwordError} />
        </div>
    );
}

export function ConfirmPasswordField({
    form,
    update,
    markTouched,
    getFieldError,
    getFieldValidationState,
    fieldErrorVariant,
}: FieldProps) {
    const confirmPasswordError = getFieldError('confirmPassword');

    return (
        <div className={fieldClass(confirmPasswordError, fieldErrorVariant)}>
            <LabelWithTooltip
                label="Confirm password"
                htmlFor="confirmPassword"
                validationState={getFieldValidationState('confirmPassword')}
            />
            <input
                id="confirmPassword"
                className={inputClass(confirmPasswordError, fieldErrorVariant)}
                type="password"
                autoComplete="new-password"
                required
                value={form.confirmPassword}
                onChange={update('confirmPassword')}
                onBlur={markTouched('confirmPassword')}
                aria-invalid={confirmPasswordError ? 'true' : undefined}
                aria-describedby={confirmPasswordError ? 'confirmPassword-error' : undefined}
            />
            <FieldError id="confirmPassword-error" message={confirmPasswordError} />
        </div>
    );
}

export function MfaFields({
    form,
    update,
    markTouched,
    getFieldError,
    getFieldValidationState,
    fieldErrorVariant,
}: FieldProps) {
    const mfaCodeError = getFieldError('mfaCode');
    const mfaRecoveryError = getFieldError('mfaRecovery');

    return (
        <div className="mfa-grid">
            <div className={fieldClass(mfaCodeError, fieldErrorVariant)}>
                <LabelWithTooltip
                    label="Authenticator code"
                    htmlFor="mfaCode"
                    validationState={getFieldValidationState('mfaCode')}
                />
                <input
                    id="mfaCode"
                    className={inputClass(mfaCodeError, fieldErrorVariant)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={form.mfaCode}
                    onChange={update('mfaCode')}
                    onBlur={markTouched('mfaCode')}
                    aria-invalid={mfaCodeError ? 'true' : undefined}
                    aria-describedby={mfaCodeError ? 'mfaCode-error' : undefined}
                />
                <FieldError id="mfaCode-error" message={mfaCodeError} />
            </div>
            <div className={fieldClass(mfaRecoveryError, fieldErrorVariant)}>
                <LabelWithTooltip
                    label="Recovery code"
                    htmlFor="mfaRecovery"
                    validationState={getFieldValidationState('mfaRecovery')}
                />
                <input
                    id="mfaRecovery"
                    className={inputClass(mfaRecoveryError, fieldErrorVariant)}
                    autoComplete="one-time-code"
                    value={form.mfaRecovery}
                    onChange={update('mfaRecovery')}
                    onBlur={markTouched('mfaRecovery')}
                    aria-invalid={mfaRecoveryError ? 'true' : undefined}
                    aria-describedby={mfaRecoveryError ? 'mfaRecovery-error' : undefined}
                />
                <FieldError id="mfaRecovery-error" message={mfaRecoveryError} />
            </div>
        </div>
    );
}

export function SubmitButton({ label, loading }: { label: string; loading: boolean }) {
    return (
        <button
            className="btn btn-lg btn-solid-blueberry w-full disabled:opacity-60"
            type="submit"
            disabled={loading}
        >
            {loading ? 'Please wait...' : label}
        </button>
    );
}
